import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import PeopleAltIcon    from "@mui/icons-material/PeopleAlt";
import ActionButton from "../../component/common/ActionButton";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { runApiAction } from "../../util/apiFeedback";
import { useMyPermissions } from "../../util/useMyPermissions";

import { ds } from "../../styles/designSystem";
import { downloadExcel } from "../../util/downloadExcel";
import { HR_NAV, tabUrl } from "../../config/moduleNav";

import HrEmployeeTab        from "./tabs/HrEmployeeTab";
import HrDepartmentsTab     from "./tabs/HrDepartmentsTab";
import HrPositionsTab       from "./tabs/HrPositionsTab";
import HrDutiesTab          from "./tabs/HrDutiesTab";
import HrAttendanceTab      from "./tabs/HrAttendanceTab";
import HrLeaveTab           from "./tabs/HrLeaveTab";
import HrSalaryTab          from "./tabs/HrSalaryTab";
import HrApprovalTab        from "./tabs/HrApprovalTab";
import HrExternalViewersTab from "./tabs/HrExternalViewersTab";

function NoPermission({ tabLabel }) {
    return (
        <Box sx={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", py: 10, color: "#94a3b8",
        }}>
            <LockOutlinedIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.4 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 600 }}>접근 권한이 없습니다</Typography>
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
                [{tabLabel}] 탭을 열람할 권한이 없습니다. 관리자에게 문의하세요.
            </Typography>
        </Box>
    );
}

export default function HrPage() {
    const { hasPerm } = useMyPermissions();
    const location = useLocation();
    const history  = useHistory();
    const [exporting, setExporting] = useState(false);

    /** 탭 key → 컴포넌트. 이름은 config(HR_NAV)에만 둔다. */
    const renderTab = (key) => {
        switch (key) {
            case "leave":      return <HrLeaveTab />;
            case "attendance": return <HrAttendanceTab />;
            case "approval":   return <HrApprovalTab />;
            case "employee":   return <HrEmployeeTab />;
            case "salary":     return <HrSalaryTab />;
            case "temp":       return <HrExternalViewersTab />;
            case "dept":       return <HrDepartmentsTab />;
            case "position":   return <HrPositionsTab />;
            case "duty":       return <HrDutiesTab />;
            default:           return null;
        }
    };

    // ?section= (기본: employee)
    const sectionKey = useMemo(() => {
        const s = new URLSearchParams(location.search).get("section");
        return HR_NAV.sections.some(x => x.key === s) ? s : "employee";
    }, [location.search]);
    const section = HR_NAV.sections.find(s => s.key === sectionKey);

    const visibleTabs = useMemo(
        () => section.tabs.filter(t => hasPerm(t.perm)),
        [section, hasPerm]
    );

    // 활성 탭은 URL(?tab=)에서 파생 → 사이드바 트리와 동기화
    const tabParam = new URLSearchParams(location.search).get("tab");
    const activeIdx = useMemo(() => {
        const i = visibleTabs.findIndex(t => t.label === tabParam);
        return i >= 0 ? i : 0;
    }, [visibleTabs, tabParam]);
    const activeLabel = visibleTabs[activeIdx]?.label;

    useEffect(() => {
        if (activeLabel && tabParam !== activeLabel) {
            history.replace(tabUrl(HR_NAV, sectionKey, activeLabel));
        }
    }, [sectionKey, tabParam, activeLabel, history]);

    const handleTabChange = (_, idx) => {
        const label = visibleTabs[idx]?.label;
        if (label) history.push(tabUrl(HR_NAV, sectionKey, label));
    };

    const handleExport = () => runApiAction({
        request: () => downloadExcel("hr"),
        successMsg: "엑셀 파일이 다운로드되었습니다.",
        errorMsg: (msg) => "엑셀 내보내기 실패: " + (msg || "알 수 없는 오류"),
        setLoading: setExporting,
    });

    if (visibleTabs.length === 0) {
        return (
            <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 3 }}>
                <NoPermission tabLabel="인사 전체" />
            </Box>
        );
    }

    const safeIdx = Math.min(activeIdx, visibleTabs.length - 1);

    return (
        <Box sx={{ minHeight: "100%", bgcolor: "#fff" }}>
        <Box sx={{ maxWidth: 1600, mx: "auto", px: 3, py: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Typography sx={ds.typography.pageTitle}>인사</Typography>
                <Box sx={{ flex: 1 }} />
                {/*//TODO 엑셀 내보내기는 추후 추에*/}
                {/*<ActionButton*/}
                {/*    variant="secondary"*/}
                {/*    startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}*/}
                {/*    onClick={handleExport}*/}
                {/*    loading={exporting}*/}
                {/*    label={exporting ? "준비 중..." : "엑셀 내보내기"}*/}
                {/*    sx={{ fontSize: 12 }}*/}
                {/*/>*/}
            </Box>
            <Typography sx={{ ...ds.typography.pageSubtitle, mb: 2.5 }}>
                {section.subtitle}
            </Typography>

            <Box sx={{ borderBottom: "1px solid #e2e8f0", mb: 2.5 }}>
                <Tabs value={safeIdx} onChange={handleTabChange}
                    sx={ds.components.tabs.root} variant="scrollable" scrollButtons="auto">
                    {visibleTabs.map((t) => <Tab key={t.key} label={t.label} />)}
                </Tabs>
            </Box>

            {renderTab(visibleTabs[safeIdx]?.key)}
        </Box>
        </Box>
    );
}
